// Inbox — single-page triage view of everything pending operator action.
//
// Aggregates 6 queues (pending bookings, vendor KYC, reviews, requested
// payouts, listings, open inquiries) with the top 5 of each. Each row
// has the inline action that resolves the most common case; for anything
// edge-case the row links through to the entity's detail page.
//
// This is intentionally NOT a replacement for the per-queue list pages —
// it's the "clear my day" landing page. Anything beyond the top 5 of a
// queue means jumping to that queue's full list (the "view all →" link).

import { type ReactNode } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  approveCar,
  approveReview,
  approvePayout,
  approveVendorKyc,
  cancelBooking,
  confirmBooking,
  fetchAdminInbox,
  rejectReview,
  spamReview,
  rejectCar,
  rejectVendorKyc,
  type InboxBookingRow,
  type InboxInquiryRow,
  type InboxListingRow,
  type InboxPayoutRow,
  type InboxReviewRow,
  type InboxVendorKycRow,
} from '@/lib/admin-api';

const fmtMoney = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(v);

const fmtRelative = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
};

export function Inbox() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'inbox'],
    queryFn: ({ signal }) => fetchAdminInbox(signal),
    refetchInterval: 60_000,
  });

  // Invalidate inbox + related queries on any mutation so the row vanishes
  // after action (and the matching list/dashboard counts refresh).
  const invalidate = (extraKey?: string[]) => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'inbox'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'attention'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    if (extraKey) {
      void queryClient.invalidateQueries({ queryKey: extraKey });
    }
  };

  const confirmBookingMut = useMutation({
    mutationFn: confirmBooking,
    onSuccess: () => invalidate(['admin', 'bookings']),
  });
  const cancelBookingMut = useMutation({
    mutationFn: ({ ref, reason }: { ref: string; reason?: string }) =>
      cancelBooking(ref, reason),
    onSuccess: () => invalidate(['admin', 'bookings']),
  });

  const approveKycMut = useMutation({
    mutationFn: approveVendorKyc,
    onSuccess: () => invalidate(['admin', 'vendors']),
  });
  const rejectKycMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      rejectVendorKyc(id, reason),
    onSuccess: () => invalidate(['admin', 'vendors']),
  });

  const approveReviewMut = useMutation({
    mutationFn: approveReview,
    onSuccess: () => invalidate(['admin', 'reviews']),
  });
  const rejectReviewMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      rejectReview(id, reason),
    onSuccess: () => invalidate(['admin', 'reviews']),
  });
  const spamReviewMut = useMutation({
    mutationFn: spamReview,
    onSuccess: () => invalidate(['admin', 'reviews']),
  });

  const approvePayoutMut = useMutation({
    mutationFn: approvePayout,
    onSuccess: () => invalidate(['admin', 'payouts']),
  });

  const approveCarMut = useMutation({
    mutationFn: approveCar,
    onSuccess: () => invalidate(['admin', 'cars']),
  });
  const rejectCarMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      rejectCar(id, reason),
    onSuccess: () => invalidate(['admin', 'cars']),
  });

  const s = data?.sections;
  const grandTotal =
    (s?.bookings.total ?? 0) +
    (s?.vendorKyc.total ?? 0) +
    (s?.reviews.total ?? 0) +
    (s?.payouts.total ?? 0) +
    (s?.listings.total ?? 0) +
    (s?.inquiries.total ?? 0);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Overview"
        title="Inbox"
        sub={
          data
            ? grandTotal === 0
              ? 'Nothing pending — queues are clear.'
              : `${grandTotal.toLocaleString()} items across 6 queues need attention.`
            : 'Loading queues…'
        }
      />

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load inbox: {(error as Error).message}
        </div>
      ) : null}

      {!isLoading && grandTotal === 0 ? (
        <div className="rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] p-8 text-center text-[#065f46]">
          <div className="text-[15px] font-semibold">All clear</div>
          <div className="mt-1 text-[12.5px] opacity-80">
            No bookings, KYC, reviews, payouts, listings, or inquiries need attention right now.
          </div>
        </div>
      ) : null}

      {s ? (
        <>
          <InboxSection
            title="Pending bookings"
            viewAll="/bookings"
            section={s.bookings}
            empty="No bookings awaiting confirmation."
            renderRow={(row) => (
              <BookingRow
                row={row}
                busy={
                  (confirmBookingMut.isPending &&
                    (confirmBookingMut.variables as string) === row.reference) ||
                  (cancelBookingMut.isPending &&
                    (cancelBookingMut.variables as { ref: string }).ref === row.reference)
                }
                onView={() =>
                  void navigate({
                    to: `/bookings/${encodeURIComponent(row.reference)}` as never,
                  })
                }
                onConfirm={() => confirmBookingMut.mutate(row.reference)}
                onCancel={() => {
                  const reason =
                    window.prompt('Cancellation reason (optional):') ?? undefined;
                  cancelBookingMut.mutate({ ref: row.reference, reason });
                }}
              />
            )}
          />

          <InboxSection
            title="Vendor KYC"
            viewAll="/crew"
            section={s.vendorKyc}
            empty="No vendor KYC submissions waiting."
            renderRow={(row) => (
              <VendorKycRow
                row={row}
                busy={
                  (approveKycMut.isPending && approveKycMut.variables === row.id) ||
                  (rejectKycMut.isPending &&
                    (rejectKycMut.variables as { id: number }).id === row.id)
                }
                onView={() =>
                  void navigate({
                    to: '/crew/$id' as never,
                    params: { id: String(row.id) } as never,
                  })
                }
                onApprove={() => approveKycMut.mutate(row.id)}
                onReject={() => {
                  const reason = window.prompt('Rejection reason (optional):') ?? undefined;
                  rejectKycMut.mutate({ id: row.id, reason });
                }}
              />
            )}
          />

          <InboxSection
            title="Pending reviews"
            viewAll="/reviews"
            section={s.reviews}
            empty="No reviews in moderation."
            renderRow={(row) => (
              <ReviewRow
                row={row}
                busy={
                  (approveReviewMut.isPending && approveReviewMut.variables === row.id) ||
                  (rejectReviewMut.isPending &&
                    (rejectReviewMut.variables as { id: number }).id === row.id) ||
                  (spamReviewMut.isPending && spamReviewMut.variables === row.id)
                }
                onApprove={() => approveReviewMut.mutate(row.id)}
                onReject={() => {
                  const reason = window.prompt('Rejection reason (optional):') ?? undefined;
                  rejectReviewMut.mutate({ id: row.id, reason });
                }}
                onSpam={() => spamReviewMut.mutate(row.id)}
              />
            )}
          />

          <InboxSection
            title="Requested payouts"
            viewAll="/payouts"
            section={s.payouts}
            empty="No payouts waiting for approval."
            renderRow={(row) => (
              <PayoutRow
                row={row}
                busy={approvePayoutMut.isPending && approvePayoutMut.variables === row.id}
                onView={() =>
                  void navigate({ to: `/payouts/${row.id}` as never })
                }
                onApprove={() => approvePayoutMut.mutate(row.id)}
              />
            )}
          />

          <InboxSection
            title="Listings to review"
            viewAll="/cars"
            section={s.listings}
            empty="No car listings waiting."
            renderRow={(row) => (
              <ListingRow
                row={row}
                busy={
                  (approveCarMut.isPending && approveCarMut.variables === row.id) ||
                  (rejectCarMut.isPending &&
                    (rejectCarMut.variables as { id: number }).id === row.id)
                }
                onView={() => void navigate({ to: `/cars/${row.id}` as never })}
                onApprove={() => approveCarMut.mutate(row.id)}
                onReject={() => {
                  const reason = window.prompt('Rejection reason (optional):') ?? undefined;
                  rejectCarMut.mutate({ id: row.id, reason });
                }}
              />
            )}
          />

          <InboxSection
            title="Open inquiries"
            viewAll="/inquiries"
            section={s.inquiries}
            empty="No open inquiry threads."
            renderRow={(row) => (
              <InquiryRow
                row={row}
                onView={() =>
                  void navigate({ to: `/inquiries/${row.id}` as never })
                }
              />
            )}
          />
        </>
      ) : null}
    </div>
  );
}

// ─── Section shell ───────────────────────────────────────────────────

function InboxSection<Row extends { id: number }>({
  title,
  viewAll,
  section,
  renderRow,
  empty,
}: {
  title: string;
  viewAll: string;
  section: { total: number; rows: Row[] };
  renderRow: (row: Row) => ReactNode;
  empty: string;
}) {
  // Hide entirely when total = 0; an inbox shouldn't show empty sections.
  if (section.total === 0) return null;
  const remaining = section.total - section.rows.length;
  return (
    <section className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <header className="flex items-baseline justify-between gap-3 border-b border-[color:var(--bo-line)] px-5 py-3">
        <div>
          <h2 className="text-[14px] font-semibold tracking-tight">
            {title}
            <span className="ms-2 rounded-md bg-[#fef2f2] px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-[#9f1239]">
              {section.total.toLocaleString()}
            </span>
          </h2>
        </div>
        <Link
          to={viewAll as never}
          className="text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
        >
          {remaining > 0 ? `View all ${section.total}` : 'View queue'} →
        </Link>
      </header>
      {section.rows.length === 0 ? (
        <div className="px-5 py-6 text-center text-[12.5px] text-[color:var(--bo-mute)]">
          {empty}
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--bo-line)]">
          {section.rows.map((row) => (
            <li key={row.id} className="px-5 py-3">
              {renderRow(row)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Row renderers ───────────────────────────────────────────────────

function RowShell({
  primary,
  secondary,
  meta,
  actions,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  meta?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium text-[color:var(--bo-ink)]">{primary}</div>
        {secondary ? (
          <div className="mt-0.5 truncate text-[11.5px] text-[color:var(--bo-mute)]">
            {secondary}
          </div>
        ) : null}
      </div>
      {meta ? (
        <div className="shrink-0 text-[11.5px] tabular-nums text-[color:var(--bo-mute)]">
          {meta}
        </div>
      ) : null}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div>
    </div>
  );
}

function BookingRow({
  row,
  busy,
  onConfirm,
  onCancel,
  onView,
}: {
  row: InboxBookingRow;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onView: () => void;
}) {
  return (
    <RowShell
      primary={
        <>
          <span className="font-mono text-[12px]">{row.reference}</span>
          <span className="ms-2">{fmtMoney(row.total, row.currency)}</span>
        </>
      }
      secondary={`${row.customer} · ${row.car ?? '—'}`}
      meta={`${fmtRelative(row.createdAt)} ago`}
      actions={
        <>
          <RowAction kind="primary" busy={busy} onClick={onConfirm}>
            Confirm
          </RowAction>
          <RowAction kind="danger" busy={busy} onClick={onCancel}>
            Cancel
          </RowAction>
          <RowAction busy={busy} onClick={onView}>
            Open
          </RowAction>
        </>
      }
    />
  );
}

function VendorKycRow({
  row,
  busy,
  onApprove,
  onReject,
  onView,
}: {
  row: InboxVendorKycRow;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onView: () => void;
}) {
  return (
    <RowShell
      primary={row.name}
      secondary={row.email}
      meta={`${fmtRelative(row.createdAt)} ago`}
      actions={
        <>
          <RowAction kind="primary" busy={busy} onClick={onApprove}>
            Approve
          </RowAction>
          <RowAction kind="danger" busy={busy} onClick={onReject}>
            Reject
          </RowAction>
          <RowAction busy={busy} onClick={onView}>
            Review
          </RowAction>
        </>
      }
    />
  );
}

function ReviewRow({
  row,
  busy,
  onApprove,
  onReject,
  onSpam,
}: {
  row: InboxReviewRow;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onSpam: () => void;
}) {
  return (
    <RowShell
      primary={
        <>
          <span className="font-semibold tabular-nums">★ {row.rating.toFixed(1)}</span>
          {row.title ? <span className="ms-2 font-medium">{row.title}</span> : null}
        </>
      }
      secondary={
        <span className="line-clamp-1">
          {row.customer} · {row.car ?? '—'}
          {row.body ? ` — ${row.body}` : ''}
        </span>
      }
      meta={`${fmtRelative(row.createdAt)} ago`}
      actions={
        <>
          <RowAction kind="primary" busy={busy} onClick={onApprove}>
            Approve
          </RowAction>
          <RowAction busy={busy} onClick={onReject}>
            Reject
          </RowAction>
          <RowAction kind="danger" busy={busy} onClick={onSpam}>
            Spam
          </RowAction>
        </>
      }
    />
  );
}

function PayoutRow({
  row,
  busy,
  onApprove,
  onView,
}: {
  row: InboxPayoutRow;
  busy: boolean;
  onApprove: () => void;
  onView: () => void;
}) {
  return (
    <RowShell
      primary={
        <>
          <span className="font-mono text-[12px]">{row.reference}</span>
          <span className="ms-2 font-semibold tabular-nums">
            {fmtMoney(row.amount, row.currency)}
          </span>
        </>
      }
      secondary={`${row.vendor} · ${new Date(row.periodFrom).toLocaleDateString()} → ${new Date(
        row.periodTo,
      ).toLocaleDateString()}`}
      meta={`${fmtRelative(row.createdAt)} ago`}
      actions={
        <>
          <RowAction kind="primary" busy={busy} onClick={onApprove}>
            Approve
          </RowAction>
          <RowAction busy={busy} onClick={onView}>
            Review
          </RowAction>
        </>
      }
    />
  );
}

function ListingRow({
  row,
  busy,
  onApprove,
  onReject,
  onView,
}: {
  row: InboxListingRow;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onView: () => void;
}) {
  return (
    <RowShell
      primary={row.title}
      secondary={`${row.vendor ?? '—'}${row.price != null ? ` · ${fmtMoney(row.price)}` : ''}`}
      meta={`${fmtRelative(row.createdAt)} ago`}
      actions={
        <>
          <RowAction kind="primary" busy={busy} onClick={onApprove}>
            Approve
          </RowAction>
          <RowAction busy={busy} onClick={onReject}>
            Reject
          </RowAction>
          <RowAction busy={busy} onClick={onView}>
            Open
          </RowAction>
        </>
      }
    />
  );
}

function InquiryRow({
  row,
  onView,
}: {
  row: InboxInquiryRow;
  onView: () => void;
}) {
  return (
    <RowShell
      primary={row.subject}
      secondary={`${row.customer} ↔ ${row.vendor ?? '—'}${row.car ? ` · ${row.car}` : ''}`}
      meta={`Updated ${fmtRelative(row.updatedAt)} ago`}
      actions={<RowAction onClick={onView}>Open thread</RowAction>}
    />
  );
}

